import { useCallback, useState, useMemo } from 'react';

import { type OpDefData } from '@frontend/api';
import {
  VStack,
  HStack,
  IconButton,
  CloseIcon,
  Button,
} from '@frontend/legacy-components';

import OpDefSelector from 'modules/common/smartComponents/opDefSelector/OpDefSelector';

type MultiOpDefSelectorProps = {
  selectedOpDefIds: number[];
  onChange: (opDefIds: number[], displayValues: Record<string, string>) => void;
  onClear: (e?: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  onBlur?: (e: React.FocusEvent) => void;
  name?: string;
};

export const MultiOpDefSelector = ({
  selectedOpDefIds,
  onChange,
  onClear,
  disabled = false,
  onBlur,
  name,
}: MultiOpDefSelectorProps) => {
  const [isAddingNewOpDef, setIsAddingNewOpDef] = useState(false);

  const handleAddOpDef = useCallback(() => {
    setIsAddingNewOpDef(true);
  }, []);

  const handleRemoveOpDef = useCallback(
    (index: number) => {
      const newOpDefIds = selectedOpDefIds.filter((_, i) => i !== index);

      // If removing the last op def, clear the whole entry
      if (newOpDefIds.length === 0) {
        onChange([], {});
        setIsAddingNewOpDef(false);
        return;
      }

      onChange(newOpDefIds, {});
    },
    [selectedOpDefIds, onChange],
  );

  const handleChangeOpDef = useCallback(
    (index: number, rawValues: OpDefData[]) => {
      const newOpDefIds = [...selectedOpDefIds];

      // Single selection - take the first (and only) value
      const selectedOpDef = rawValues[0];
      const displayValues: Record<string, string> = {};
      if (selectedOpDef) {
        newOpDefIds[index] = selectedOpDef.id;
        setIsAddingNewOpDef(false);
        displayValues[String(selectedOpDef.id)] = selectedOpDef.name;
      }

      onChange(newOpDefIds, displayValues);
    },
    [selectedOpDefIds, onChange],
  );

  const handleCancelNewOpDef = useCallback(() => {
    setIsAddingNewOpDef(false);
  }, []);

  // Determine which rows to display
  const rowsToDisplay = useMemo(() => {
    const rows = [...selectedOpDefIds];
    if (isAddingNewOpDef || selectedOpDefIds.length === 0) {
      rows.push(-1); // Placeholder for new row
    }
    return rows;
  }, [selectedOpDefIds, isAddingNewOpDef]);

  return (
    <VStack spacing={1} alignItems="stretch" width="100%">
      {rowsToDisplay.map((opDefId, index) => {
        const isNewRow = opDefId === -1;
        return (
          <HStack
            key={isNewRow ? 'new-opdef' : `opdef-${index}`}
            alignItems="start"
            spacing={1}
            className="multi-opdef-selector-row"
          >
            <OpDefSelector
              name={name && !isNewRow ? `${name}.${index}` : undefined}
              disabled={disabled}
              placeholder="Select operation"
              allowMultiSelect={false}
              selectedValues={!isNewRow && opDefId ? [opDefId] : []}
              isClearable={false}
              onChange={(rawValues) =>
                handleChangeOpDef(index, rawValues as OpDefData[])
              }
              onBlur={onBlur}
            />
            <IconButton
              size="sm"
              m={0}
              mt={0.5}
              flexShrink={0}
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                // to prevent intermittent form submission when clicking clear
                e.stopPropagation();
                if (selectedOpDefIds.length === 0) {
                  onClear();
                } else if (isNewRow) {
                  handleCancelNewOpDef();
                } else {
                  handleRemoveOpDef(index);
                }
              }}
              isDisabled={disabled}
              aria-label="Remove op def"
              className="multi-opdef-selector-remove-button"
            >
              <CloseIcon />
            </IconButton>
          </HStack>
        );
      })}
      {!isAddingNewOpDef && selectedOpDefIds.length > 0 && (
        <Button
          variant="secondary"
          size="sm"
          onClick={handleAddOpDef}
          isDisabled={disabled}
          alignSelf="flex-start"
          className="multi-opdef-selector-add-button"
        >
          ADD
        </Button>
      )}
    </VStack>
  );
};
